import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function ExaminationCenterLoginPage() {
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', full_name: '', center_name: '' });

  if (isAuthenticated) {
    navigate('/dashboard/examination-center');
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back');
      navigate('/dashboard/examination-center');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.full_name, 'examination_center');
      toast.success('Account created successfully');
      navigate('/dashboard/examination-center');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="exam-center-login-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1B2A4A] rounded-sm flex items-center justify-center mx-auto mb-3">
            <MapPin className="h-7 w-7 text-[#C5A572]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] font-heading">Examination Center Portal</h1>
          <p className="text-sm text-slate-500 mt-1">Access your examination center dashboard</p>
        </div>

        <Card className="imcac-card">
          <CardContent className="p-6">
            <Tabs defaultValue="login">
              <TabsList className="w-full mb-6 bg-[#F1F5F9]">
                <TabsTrigger value="login" className="flex-1 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="ec-login-tab">
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="register" className="flex-1 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="ec-register-tab">
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="ec-login-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                    <Input
                      id="ec-login-email"
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="mt-1 rounded-sm"
                      placeholder="center@example.com"
                      required
                      data-testid="ec-login-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ec-login-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="ec-login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="rounded-sm pr-10"
                        placeholder="Enter your password"
                        required
                        data-testid="ec-login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-[#1B2A4A] text-white rounded-sm" disabled={loading} data-testid="ec-login-submit">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="ec-reg-name" className="text-sm font-medium text-slate-700">Contact Person Name</Label>
                    <Input
                      id="ec-reg-name"
                      type="text"
                      value={registerForm.full_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                      className="mt-1 rounded-sm"
                      placeholder="Dr. John Smith"
                      required
                      data-testid="ec-reg-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ec-reg-center" className="text-sm font-medium text-slate-700">Center Name</Label>
                    <Input
                      id="ec-reg-center"
                      type="text"
                      value={registerForm.center_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, center_name: e.target.value })}
                      className="mt-1 rounded-sm"
                      placeholder="ABC Medical Institute"
                      data-testid="ec-reg-center"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ec-reg-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                    <Input
                      id="ec-reg-email"
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="mt-1 rounded-sm"
                      placeholder="center@example.com"
                      required
                      data-testid="ec-reg-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ec-reg-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="ec-reg-password"
                        type={showPassword ? 'text' : 'password'}
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className="rounded-sm pr-10"
                        placeholder="Create a password"
                        required
                        minLength={6}
                        data-testid="ec-reg-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-[#C5A572] text-white rounded-sm" disabled={loading} data-testid="ec-reg-submit">
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          Already have an approved examination center?{' '}
          <a href="/apply/examination-center" className="text-[#C5A572] hover:underline">Apply for accreditation</a>
        </p>
      </div>
    </div>
  );
}
